import { useState } from 'react';
import useAsyncEffect from 'use-async-effect';
import { logError, logInfo } from '../shared/logger';
import { HttpError } from '../shared/httpClient';
import type { Redirect } from '../shared/types/Redirect';
import fetchDeepLink from './fetchDeepLink';
import redirectTo, { isRedirectDisabled } from './redirectTo';

const useRedirect = (
  redirect: Redirect,
  isGhostMode: boolean,
  customTimeout?: number,
): [string, NodeJS.Timeout | undefined, boolean] => {
  const [redirectUrl, setRedirectUrl] = useState(redirect.fallbackUrl);
  const [redirectTimer, setRedirectTimer] = useState<NodeJS.Timeout>();
  const [resolvedGhostMode, setResolvedGhostMode] = useState(isGhostMode);

  useAsyncEffect(
    async (isMounted) => {
      let result: { url: string; ghostMode?: boolean } = { url: redirectUrl };
      try {
        result = await fetchDeepLink(redirect);

        logInfo('Deep link fetched', { campaign: redirect.campaign });
      } catch (error) {
        const message = (error && (error as Error)?.message) || 'Unknown';
        const httpStatus = error instanceof HttpError ? { http_status_i: error.status } : {};

        logError(`Deep link fetch failed with error: ${message}`, { campaign: redirect.campaign, ...httpStatus });
      }

      if (isMounted()) {
        setRedirectUrl(result.url);

        const ghostMode = isGhostMode || result.ghostMode === true;

        if (result.ghostMode !== undefined) {
          logInfo(`Ghost mode resolved from server header: ${result.ghostMode}`, { campaign: redirect.campaign });
        }

        setResolvedGhostMode(ghostMode);

        if (window && !isRedirectDisabled(window, result.url) && !redirectTimer) {
          const timer = redirectTo(result.url, redirect, ghostMode, customTimeout);
          setRedirectTimer(timer);
        }
      }
    },
    [redirect],
  );

  return [redirectUrl, redirectTimer, resolvedGhostMode];
};

export default useRedirect;
